import { DestroyRef, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationAccessError, LocationService } from './location.service';

import { createUrl } from './api.util';
import { JwtService } from './jwt.service';
import { AuthResponse, IUser } from '../model/auth.model';
import { ApiBlankResponse } from '../model/generics';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private path = 'auth';
  private tokenKey = 'auth_token';
  private currentCompanyKey = 'currentCompanyId';
  public currentUser: IUser | null = null;
  private currentUserSubject = new BehaviorSubject<IUser | null>(null);

  constructor(
    private http: HttpClient,
    private jwtService: JwtService,
    private loadingService: LoadingService,
    private locationService: LocationService,
    destroyRef: DestroyRef,
  ) {
    this.loadToken();
    this.jwtService.currentJwtPayload$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
      if (this.jwtService.getJWTString()) this.locationService.startTracking();
      else this.locationService.stopTracking();
    });
  }

  private loadToken() {
    const token = localStorage.getItem(this.tokenKey);

    if (token) {
      this.currentUser = null;
    }
  }

  private setCurrentUser(user: IUser | null): void {
    this.currentUser = user;
    this.currentUserSubject.next(user);
    if (user?.currentCompanyId) {
      sessionStorage.setItem(this.currentCompanyKey, user.currentCompanyId);
    } else {
      sessionStorage.removeItem(this.currentCompanyKey);
    }
  }

  login(
    employeeCode: number | string,
    password: string,
    fcmToken: string,
    deviceId: string,
  ): Observable<IUser | null> {
    return this.loadingService
      .blockWithLoadingOverlayRx(
        this.locationService.getCurrentLocation().pipe(switchMap((location) => {
          if (!Number.isFinite(location.accuracy) || location.accuracy <= 0) {
            throw new LocationAccessError('A valid location could not be received. Enable location services and try again.');
          }
          // if (location.accuracy > 100) {
          //   throw new LocationAccessError('Your location accuracy is too low. Please enable precise location/GPS and try again.');
          // }
          return this.http.post<AuthResponse>(createUrl(this.path, 'login'), {
            employeeCode,
            password,
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy,
            deviceToken: fcmToken,
            deviceId,
          });
        })),
      )
      .pipe(
        tap((res) => {
          if (res.success && res.data.accessToken) {
            this.jwtService.saveJWT(res.data.accessToken);
          }
        }),
        switchMap((res) => {
          if (res.success && res.data.accessToken) {
            return this.getProfile().pipe(map((profileRes) => profileRes.data));
          }

          return of(null);
        }),
      );
  }

  switchCompany(companyId: string): Observable<{sucess: true}> {
    return this.loadingService
      .blockWithLoadingOverlayRx(
        this.http.get<{sucess: true}>(createUrl(this.path, 'switch-company'), {
          params: { companyId },
        }),
      );
  }

  forgetPassword(payload: {
    employeeCode: string;
    otp?: string;
    newPassword?: string;
  }): Observable<ApiBlankResponse> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<ApiBlankResponse>(
        createUrl(this.path, 'forget-password'),
        payload,
      ),
    );
  }

  verifyOtp(payload: {
    employeeCode: string;
    otp: string;
  }): Observable<ApiBlankResponse> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<ApiBlankResponse>(
        createUrl(this.path, 'verify-otp'),
        payload,
      ),
    );
  }

  logout(): void {
    this.jwtService.removeJWT();
    sessionStorage.removeItem(this.currentCompanyKey);
    this.setCurrentUser(null);
  }

  getCurrentCompanyId(): string | null {
    return this.getCurrentUser()?.currentCompanyId
      || sessionStorage.getItem(this.currentCompanyKey);
  }

  logoutApi(): Observable<{ success: boolean }> {
    return this.http
      .get<{ success: boolean }>(createUrl(this.path, 'log-out'))
      .pipe(
        tap(() => {
          this.logout();
        }),
        catchError((err) => {
          this.logout();
          throw err;
        }),
      );
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(this.tokenKey);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getCurrentUser(): IUser | null {
    return this.currentUserSubject.value;
  }

  getCurrentUser$(): Observable<IUser | null> {
    return this.currentUserSubject.asObservable();
  }

  loadProfileIfNeeded(): Observable<IUser | null> {
    if (!this.isAuthenticated()) {
      return of(null);
    }

    const current = this.getCurrentUser();
    if (current && current.role) {
      return of(current);
    }

    return this.getProfile().pipe(
      map((res) => (res.success ? res.data : null)),
      catchError(() => of(null)),
    );
  }

  updateProfile(
    formData: FormData,
  ): Observable<{ success: boolean; data: IUser; message: string }> {
    return this.loadingService
      .blockWithLoadingOverlayRx(
        this.http.put<{
          success: boolean;
          data: IUser;
          message: string;
        }>(createUrl(this.path, 'update-profile'), formData),
      )
      .pipe(
        tap((res) => {
          if (res.success && res.data) {
            this.setCurrentUser(res.data);
          }
        }),
      );
  }

  changePassword(payload: {
    oldPassword: string;
    newPassword: string;
  }): Observable<{ success: boolean; message: string }> {
    return this.loadingService.blockWithLoadingOverlayRx(
      this.http.post<{ success: boolean; message: string }>(
        createUrl(this.path, 'change-password'),
        payload,
      ),
    );
  }

  getProfile(): Observable<{ success: boolean; data: IUser }> {
    return this.http
      .get<{
        success: boolean;
        data: IUser;
      }>(createUrl(this.path, 'get-profile'))
      .pipe(
        tap((res) => {
          if (res.success && res.data) {
            this.setCurrentUser(res.data);
          }
        }),
      );
  }
}
